import { ReportPdfData } from '../types';
import { createEyebrow, createFooter, markdownToPdf } from '../utils';
import { PdfAssets } from '../../pdfTemplates';

/**
 * Creates the business topics pages for the PDF report
 * @param data Report data
 * @param assets Logo and font assets for the PDF
 * @param colors Color palette for styling
 * @param pageNumber Current page number
 * @param totalPages Total number of pages
 * @returns Business topics pages content for pdfmake
 */
export const createBusinessTopicsPages = (
  data: ReportPdfData,
  assets: PdfAssets | undefined,
  colors: Record<string, string>,
  pageNumber: number,
  totalPages: number
) => {
  // Define business topics
  const businessTopics = [
    'CDP Enhancement',
    'Data Governance & Compliance',
    'Tag Management',
    'Retail Media Networks'
  ];
  
  // Ensure selectedSections is an array
  const selectedSections = Array.isArray(data.selectedSections) ? data.selectedSections : [];
  
  // Filter selected topics
  const selectedBusinessTopics = businessTopics.filter(topic => {
    // Check if the topic is in the selected sections
    const isSelected = selectedSections.includes(topic);
    
    // Special handling for Tag Management - only include if explicitly selected
    if (topic === 'Tag Management') {
      return isSelected;
    }
    
    return isSelected;
  });
  
  if (selectedBusinessTopics.length === 0) {
    return [];
  }
  
  // Create content for each topic
  const topicPages: any[] = [];
  let currentPageNumber = pageNumber;
  
  // Add each selected business topic
  selectedBusinessTopics.forEach((topic, index) => {
    // Default content for each topic
    let topicIntro = '';
    let topicContent: any[] = [];
    
    switch (topic) {
      case 'CDP Enhancement':
        topicIntro = 'MetaRouter enhances Customer Data Platforms by providing real-time, high-quality data from every customer interaction, including previously overlooked anonymous users, thereby improving Return on Ad Spend (ROAS).';
        topicContent = [
          {
            text: 'Unified Customer Profiles',
            fontSize: 14,
            bold: true,
            color: colors.black,
            margin: [0, 0, 0, 8]
          },
          {
            text: 'MetaRouter helps create comprehensive customer profiles by collecting and unifying data from all touchpoints, including web, mobile, and server-side events.',
            fontSize: 9,
            color: colors.darkGrey,
            margin: [0, 0, 0, 24]
          },
          {
            text: 'Real-time Data Activation',
            fontSize: 14,
            bold: true,
            color: colors.black,
            margin: [0, 0, 0, 8]
          },
          {
            text: 'Enable immediate personalization and targeting by streaming customer data in real-time to your CDP and marketing tools.',
            fontSize: 9,
            color: colors.darkGrey,
            margin: [0, 0, 0, 0]
          }
        ];
        break;
      case 'Data Governance & Compliance':
        topicIntro = 'MetaRouter offers full governance over customer data, ensuring compliance with data privacy regulations and aligning with best practices for data management.';
        topicContent = [
          {
            text: 'Privacy Regulation Compliance',
            fontSize: 14,
            bold: true,
            color: colors.black,
            margin: [0, 0, 0, 8]
          },
          {
            text: 'MetaRouter helps ensure compliance with GDPR, CCPA, and other privacy regulations by providing tools for consent management and data governance.',
            fontSize: 9,
            color: colors.darkGrey,
            margin: [0, 0, 0, 24]
          },
          {
            text: 'Data Quality & Security',
            fontSize: 14,
            bold: true,
            color: colors.black,
            margin: [0, 0, 0, 8]
          },
          {
            text: 'Maintain high data quality and security with server-side processing, data validation, and secure data handling practices.',
            fontSize: 9,
            color: colors.darkGrey,
            margin: [0, 0, 0, 0]
          }
        ];
        break;
      case 'Tag Management':
        topicIntro = 'MetaRouter\'s server-side tag management reduces client-side load, improving website performance and providing more accurate data collection by bypassing ad blockers.';
        topicContent = [
          {
            text: 'Performance Optimization',
            fontSize: 14,
            bold: true,
            color: colors.black,
            margin: [0, 0, 0, 8]
          },
          {
            text: 'Improve website performance by moving tags and tracking scripts from the browser to the server, reducing page load times and improving user experience.',
            fontSize: 9,
            color: colors.darkGrey,
            margin: [0, 0, 0, 24]
          },
          {
            text: 'Ad Blocker Resilience',
            fontSize: 14,
            bold: true,
            color: colors.black,
            margin: [0, 0, 0, 8]
          },
          {
            text: 'Ensure complete data collection by bypassing client-side ad blockers and browser limitations with server-side tag management.',
            fontSize: 9,
            color: colors.darkGrey,
            margin: [0, 0, 0, 0]
          }
        ];
        break;
      case 'Retail Media Networks':
        topicIntro = 'MetaRouter enables Retail Media Networks to unlock anonymous-to-known measurement and provide rich brand-level insights, enhancing the value of media networks.';
        topicContent = [
          {
            text: 'Enhanced Measurement',
            fontSize: 14,
            bold: true,
            color: colors.black,
            margin: [0, 0, 0, 8]
          },
          {
            text: 'Provide advertisers with comprehensive measurement capabilities that connect online and offline customer journeys, demonstrating the true impact of retail media campaigns.',
            fontSize: 9,
            color: colors.darkGrey,
            margin: [0, 0, 0, 24]
          },
          {
            text: 'First-Party Data Activation',
            fontSize: 14,
            bold: true,
            color: colors.black,
            margin: [0, 0, 0, 8]
          },
          {
            text: 'Leverage first-party data to create high-value audience segments for targeting while maintaining privacy compliance.',
            fontSize: 9,
            color: colors.darkGrey,
            margin: [0, 0, 0, 0]
          }
        ];
        break;
    }
    
    // Get topic content from markdown if available
    if (data.sectionContent && data.sectionContent[topic]) {
      const customContent = markdownToPdf(data.sectionContent[topic]);
      if (customContent.length > 0) {
        topicContent = customContent;
      }
    }
    
    // Create the topic page
    topicPages.push([
      // Eyebrow section
      ...createEyebrow(topic, assets?.markUrl, colors),
      
      // Content section
      {
        stack: [
          {
            text: topic,
            fontSize: 22,
            bold: true,
            color: colors.black,
            margin: [64, 48, 0, 32]
          },
          
          // Topic intro
          {
            text: topicIntro,
            fontSize: 9,
            bold: true,
            color: colors.darkGrey,
            margin: [64, 0, 64, 24],
            alignment: 'justify'
          },
          
          // Divider
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 467,
                y2: 0,
                lineWidth: 1,
                lineColor: colors.lightGrey
              }
            ],
            margin: [64, 0, 0, 24]
          },
          
          // Two column content
          {
            columns: [
              {
                stack: topicContent.slice(0, Math.ceil(topicContent.length / 2)),
                width: '50%',
                margin: [0, 0, 12, 0]
              },
              {
                stack: topicContent.slice(Math.ceil(topicContent.length / 2)),
                width: '50%',
                margin: [12, 0, 0, 0]
              }
            ],
            margin: [64, 0, 40, 0]
          }
        ]
      },
      
      // Footer
      createFooter(currentPageNumber, totalPages, colors),
      
      // Page break (except for last topic)
      index < selectedBusinessTopics.length - 1 ? { text: '', pageBreak: 'after' } : null
    ]);
    
    currentPageNumber++;
  });
  
  // Flatten the array of pages and add a final page break
  const result = topicPages.flat().filter(Boolean);
  if (result.length > 0) {
    result.push({ text: '', pageBreak: 'after' });
  }
  
  return result;
};
